import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import compact from 'lodash/compact'
import difference from 'lodash/difference'
import uniq from 'lodash/uniq'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import UserSelector from 'modules/core/components/UserSelector'
import { Fields } from 'modules/core/components/UserSelector/_filters/filterFields'
import createMemo from 'modules/core/hooks/createMemo'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import useSelectIds from 'modules/core/hooks/useSelectIds'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import goals from 'modules/goals/store/collections/goals'
import goalsConfig from 'modules/goals/store/collections/goalsconfig'
import type GoalConfig from 'modules/goals/store/models/GoalConfig'

import { filterReportees, filterSelf, filterTeam } from './filtering'

const filteredUsers = createMemo<
  (ownerIds: number[], contractVersions: ContractVersions) => User[]
>((ownerIds, contractVersions) => {
  const employee = users.current.employee

  if (!employee || users.current.policy.can('goals.deprecatedCreateForEverybody')) {
    const userIds = users.map((user) => user.employee && user.attributes.id)
    const filteredIds = difference(userIds, ownerIds)

    return compact(filteredIds.map((id) => users.get(id)))
  }

  let list: number[] = []
  list = filterSelf(list, employee)
  list = filterReportees(list, employee, contractVersions)
  list = filterTeam(list, employee, contractVersions)

  const filteredIds = difference(list, ownerIds)

  return uniq(compact(filteredIds.map((id) => users.get(id))))
})
interface ModalProps {
  goalConfig: GoalConfig
  onClose: () => void
  contractVersions: ContractVersions
}

const OwnersModal = ({ goalConfig, onClose, contractVersions }: ModalProps) => {
  const ownerIds = goalConfig.attributes.owner_ids
  const [selectedIds, setSelectedId, setSelectedIds] = useSelectIds()

  const { performAction } = useAsyncAction(async () => {
    await goals.bulkCreate(selectedIds, goalConfig.attributes.id)
    await Promise.all([
      goals.fetch({
        data: {
          goal_config_id: goalConfig.attributes.id,
        },
      }),
      goalsConfig.fetch(),
    ])

    return onClose()
  })

  return (
    <Modal
      title={i18n.t('surveys.goals.show.modal.title')}
      description={i18n.t('surveys.goals.show.modal.description')}
      illustration="people-add"
      onClose={onClose}
      onSubmit={performAction}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.close')}
          </Button>
          <Button disabled={!selectedIds.length} submit>
            {i18n.t('surveys.goals.show.modal.title')}
          </Button>
        </>
      }
    >
      <UserSelector
        users={filteredUsers(ownerIds, contractVersions)}
        selectedIds={selectedIds}
        onSelect={setSelectedId}
        onSelectMultiple={setSelectedIds}
        hideFields={[Fields.EmployeeStatus, Fields.UserType]}
        contractVersions={contractVersions}
      />
    </Modal>
  )
}

export default observer(OwnersModal)
